'use client';

import { useEffect, useState } from 'react';
import { Link as LinkIcon, MessageCircle, Facebook } from 'lucide-react';

export type ShareCruiseSectionProps = {
  title: string;
  /**
   * UI variant for reusing the share controls in different page sections.
   * - `default`: includes heading + title text
   * - `header`: compact controls for the cruise header box
   */
  variant?: 'default' | 'header';
  /**
   * Optional override for the URL (useful for tests).
   * If not provided, the component uses `window.location.href`.
   */
  url?: string;
};

export default function ShareCruiseSection({ title, variant = 'default', url }: ShareCruiseSectionProps) {
  const [currentUrl, setCurrentUrl] = useState<string>(() => {
    if (url) return url;
    if (typeof window === 'undefined') return '';
    return window.location.href;
  });
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (url) {
      setCurrentUrl(url);
      return;
    }
    if (typeof window === 'undefined') return;
    setCurrentUrl(window.location.href);
  }, [url]);

  const handleCopyLink = async () => {
    const targetUrl =
      url ||
      currentUrl ||
      (typeof window !== 'undefined' ? window.location.href : '');
    if (!targetUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(targetUrl);
      } else {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = targetUrl;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setToast('Link copied!');
      window.setTimeout(() => setToast(null), 1600);
    } catch {
      setToast('Copy failed');
      window.setTimeout(() => setToast(null), 1600);
    }
  };

  const handleWhatsAppShare = () => {
    const targetUrl =
      url ||
      currentUrl ||
      (typeof window !== 'undefined' ? window.location.href : '');
    if (!targetUrl) return;

    const shareText = `${title} - ${targetUrl}`;
    const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    // Open in a new tab/window.
    window.open(waHref, '_blank', 'noopener,noreferrer');
  };

  const handleFacebookShare = () => {
    const targetUrl =
      url ||
      currentUrl ||
      (typeof window !== 'undefined' ? window.location.href : '');
    if (!targetUrl) return;

    const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      targetUrl,
    )}&quote=${encodeURIComponent(title)}`;
    window.open(fbHref, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'header') {
    // Compact inline buttons for the cruise header box.
    return (
      <div className="flex flex-row flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleFacebookShare}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200"
        >
          <Facebook size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-sm font-medium whitespace-normal leading-tight text-center">Facebook</span>
        </button>

        <button
          type="button"
          onClick={handleWhatsAppShare}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200"
        >
          <MessageCircle size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-sm font-medium whitespace-normal leading-tight text-center">WhatsApp</span>
        </button>

        <button
          type="button"
          onClick={handleCopyLink}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200 mx-auto sm:mx-0"
        >
          <LinkIcon size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-sm font-medium whitespace-normal leading-tight text-center">Share link</span>
        </button>
      </div>
    );
  }

  return (
    <section className="w-full">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-100">
            Share this cruise
          </h2>
        </div>

        {toast && (
          <div
            role="status"
            aria-live="polite"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            {toast}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="text-sm text-slate-700 dark:text-slate-200">
            <span className="font-medium">Title:</span> {title}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleWhatsAppShare}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200"
            >
              <MessageCircle size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm font-medium">WhatsApp</span>
            </button>

            <button
              type="button"
              onClick={handleFacebookShare}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200"
            >
              <Facebook size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm font-medium">Facebook</span>
            </button>

            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-[#EFF6FF] dark:hover:bg-slate-800 px-3 py-2 transition-colors text-slate-700 dark:text-slate-200"
            >
              <LinkIcon size={18} className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm font-medium">Copy link</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

